import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAllDamageReports } from '../api/admin';
import styles from './CarshareDamageReports.module.css';

const sourceLabels = { carshare: 'Carshare', inspection: 'Inspection' };
const sourceBadge = { carshare: '#f59e0b', inspection: '#6366f1' };
const phaseLabels = { pre_trip: 'Pre-trip', post_trip: 'Post-trip' };

export default function AllDamageReports() {
  const [selected, setSelected] = useState(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['all-damage-reports'],
    queryFn: () => getAllDamageReports().then((r) => r.data),
  });

  const reports = data?.reports || [];

  if (isLoading) return <div className={styles.page}><p>Loading…</p></div>;
  if (error) return <div className={styles.page}><p className={styles.error}>Failed to load damage reports.</p></div>;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>All Damage Reports</h1>
      <p className={styles.subtitle}>Every reported damage in one place: carshare trips and vehicle inspections</p>

      {reports.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>✅</div>
          <p>No damage reports yet.</p>
        </div>
      ) : (
        <div className={styles.list}>
          {reports.map((r) => {
            const v = r.vehicle || {};
            const u = r.reporter || {};
            const b = r.booking || {};
            return (
              <div key={`${r.source}-${r.id}`} className={styles.card} onClick={() => setSelected(r)}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardVehicle}>{v.make} {v.model} · {v.license_plate || '—'}</div>
                  <div className={styles.cardPhase} style={{ background: sourceBadge[r.source] || '#64748b' }}>
                    {sourceLabels[r.source] || r.source}
                  </div>
                </div>
                <div className={styles.cardMeta}>
                  <span>Reporter: {u.full_name || u.email || '—'}</span>
                  <span>Booking: #{String(b.id || '').slice(0, 8).toUpperCase()}</span>
                </div>
                <div className={styles.cardMeta}>
                  <span>{new Date(r.timestamp).toLocaleString()}</span>
                  <span>Status: {r.status || '—'}</span>
                  {r.phase && <span>Phase: {phaseLabels[r.phase] || r.phase}</span>}
                  {r.gps && <span>GPS: {Number(r.gps.lat).toFixed(5)}, {Number(r.gps.lng).toFixed(5)}</span>}
                </div>
                <p className={styles.cardDesc}>{r.description}</p>
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <div className={styles.overlay} onClick={() => setSelected(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Damage Report Detail</h2>
              <button className={styles.closeBtn} onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.detailRow}>
                <span>Source:</span>
                <span>{sourceLabels[selected.source] || selected.source}</span>
              </div>
              <div className={styles.detailRow}>
                <span>Vehicle:</span>
                <span>{selected.vehicle?.make} {selected.vehicle?.model} · {selected.vehicle?.license_plate}</span>
              </div>
              <div className={styles.detailRow}>
                <span>Reporter:</span>
                <span>{selected.reporter?.full_name} ({selected.reporter?.email})</span>
              </div>
              <div className={styles.detailRow}>
                <span>Booking:</span>
                <span>#{String(selected.booking?.id || '').slice(0, 8).toUpperCase()} — {selected.booking?.status}</span>
              </div>
              <div className={styles.detailRow}>
                <span>Submitted:</span>
                <span>{new Date(selected.timestamp).toLocaleString()}</span>
              </div>
              <div className={styles.detailRow}>
                <span>Status:</span>
                <span>{selected.status || '—'}</span>
              </div>
              {selected.gps && (
                <div className={styles.detailRow}>
                  <span>GPS:</span>
                  <span>{Number(selected.gps.lat).toFixed(5)}, {Number(selected.gps.lng).toFixed(5)}</span>
                </div>
              )}
              <div className={styles.detailRow}>
                <span>Description:</span>
                <span>{selected.description}</span>
              </div>
              {Array.isArray(selected.photos) && selected.photos.length > 0 && (
                <div className={styles.photosSection}>
                  <h3 className={styles.photosTitle}>Photos</h3>
                  <div className={styles.photosGrid}>
                    {selected.photos.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noreferrer" className={styles.photoItem}>
                        <img src={url} alt="" className={styles.photo} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
